"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { AnimatePresence, m } from "motion/react";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import { TVOverlay } from "@/components/tv-overlay";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DEFAULT_PROFILE_BACKGROUND,
  DEFAULT_PROFILE_CHARACTER,
  PROFILE_BACKGROUNDS,
  PROFILE_CHARACTERS,
} from "@/constants/profile";
import { useCurrentProfile, useSetAppAvatarParts } from "@/hooks/profile";
import { useSaveUsername } from "@/hooks/profile/use-save-username";
import { useToast } from "@/hooks/use-toast";
import type { ProfileRecord, SetUsernameResult } from "@henlo/core-supabase";

interface ProfileSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Fighter select screen styles - THJ compound shadow engineering
const fighterStyles = {
  // Character portrait tile (grid items)
  portrait: {
    base: {
      background: "linear-gradient(145deg, #2a2a32 0%, #1a1a22 100%)",
      border: "3px solid rgba(0,0,0,0.6)",
      boxShadow: `
        0 2px 4px rgba(0,0,0,0.4),
        0 1px 2px rgba(0,0,0,0.5),
        inset 0 1px 1px rgba(255,255,255,0.1),
        inset 0 -1px 1px rgba(0,0,0,0.5),
        0 0 0 1px rgba(0,0,0,0.3)
      `,
    },
    selected: {
      border: "3px solid #F5D155",
      boxShadow: `
        0 2px 4px rgba(0,0,0,0.4),
        0 1px 2px rgba(0,0,0,0.5),
        inset 0 1px 1px rgba(255,255,255,0.2),
        inset 0 -1px 1px rgba(0,0,0,0.5),
        0 0 20px rgba(245,209,85,0.4),
        0 0 40px rgba(245,209,85,0.2)
      `,
    },
    hover: {
      border: "3px solid rgba(245,209,85,0.6)",
      transform: "scale(1.05) translateY(-2px)",
      boxShadow: `
        0 4px 8px rgba(0,0,0,0.5),
        0 2px 4px rgba(0,0,0,0.6),
        inset 0 1px 1px rgba(255,255,255,0.15),
        inset 0 -1px 1px rgba(0,0,0,0.5),
        0 0 15px rgba(245,209,85,0.3)
      `,
    },
  },
  // Background thumbnail tile
  stage: {
    base: {
      background: "linear-gradient(145deg, #1a1a22 0%, #0a0a12 100%)",
      border: "2px solid rgba(0,0,0,0.4)",
      boxShadow: `
        0 1px 2px rgba(0,0,0,0.4),
        inset 0 1px 1px rgba(255,255,255,0.05),
        inset 0 -1px 1px rgba(0,0,0,0.5)
      `,
    },
    selected: {
      border: "2px solid #F5D155",
      boxShadow: `
        0 1px 2px rgba(0,0,0,0.4),
        inset 0 1px 1px rgba(255,255,255,0.1),
        inset 0 -1px 1px rgba(0,0,0,0.5),
        0 0 10px rgba(245,209,85,0.3)
      `,
    },
  },
  // Action buttons
  button: {
    skip: {
      background: "linear-gradient(145deg, #3a3a42 0%, #2a2a32 100%)",
      border: "3px solid rgba(0,0,0,0.5)",
      boxShadow: `
        0 3px 0 rgba(0,0,0,0.4),
        0 2px 4px rgba(0,0,0,0.5),
        inset 0 1px 1px rgba(255,255,255,0.1),
        inset 0 -1px 1px rgba(0,0,0,0.5)
      `,
    },
    start: {
      background: "linear-gradient(145deg, #F5D155 0%, #C4A62F 100%)",
      border: "3px solid #000000",
      boxShadow: `
        0 4px 0 rgba(0,0,0,0.4),
        0 2px 6px rgba(0,0,0,0.5),
        inset 0 1px 0 rgba(255,255,255,0.4),
        inset 0 -1px 0 rgba(0,0,0,0.3),
        0 0 20px rgba(245,209,85,0.2)
      `,
    },
  },
};

// Character names for display
const CHARACTER_NAMES = ["DURRR", "HMMM", "SCHEME"];
const STAGE_NAMES = ["BLUE ZONE", "CYBER ARENA", "GREEN FIELD", "GOLDEN PALACE", "DULL FACTORY"];

// Username normalization (matching backend)
const normalizeUsername = (u: string) =>
  u
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_") // spaces -> underscore
    .replace(/[^a-z0-9_\-.]/g, ""); // allow a-z, 0-9, _ - .

export function ProfileSetupDialog({ open, onOpenChange }: ProfileSetupDialogProps) {
  const { profile } = useCurrentProfile();
  const [username, setUsernameInput] = useState("");
  const [characterIndex, setCharacterIndex] = useState(0);
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [_isCheckingUsername, _setIsCheckingUsername] = useState(false);

  const { mutate: saveUsername } = useSaveUsername();
  const setParts = useSetAppAvatarParts();
  const { showToast } = useToast();

  // Current selections
  const character = PROFILE_CHARACTERS[characterIndex];
  const background = PROFILE_BACKGROUNDS[backgroundIndex];

  // Normalize username for checking availability
  const normalizedUsername = useMemo(
    () => normalizeUsername(username),
    [username],
  );

  // Check username availability via React Query
  // React Query handles request deduplication automatically - no debounce needed
  const {
    data: usernameCheck,
    isFetching: isCheckingUsername,
  } = useReactQuery({
    queryKey: ["username-availability", normalizedUsername, profile?.id],
    queryFn: async () => {
      if (!normalizedUsername || normalizedUsername.length < 2) return null;
      const params = new URLSearchParams({ username: normalizedUsername });
      if (profile?.id) params.set("exclude", profile.id);
      const response = await fetch(`/api/profile/username?${params.toString()}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`Failed to check username (${response.status})`);
      }
      return (await response.json()) as { profile: ProfileRecord | null; normalized: string | null };
    },
    enabled: Boolean(normalizedUsername && normalizedUsername.length >= 2),
    staleTime: 5_000, // Match API revalidation time
  });

  // Determine username availability
  const usernameStatus = useMemo(() => {
    const trimmed = username.trim();
    if (!trimmed) return null;
    if (trimmed.length < 2) return "too-short";
    if (trimmed.length > 32) return "too-long";
    if (isCheckingUsername) return "checking";
    const takenProfileId = usernameCheck?.profile?.id;
    if (takenProfileId && takenProfileId !== profile?.id) return "taken";
    return "available";
  }, [
    username,
    isCheckingUsername,
    usernameCheck?.profile?.id,
    profile?.id,
  ]);

  useEffect(() => {
    if (profile?.username) setUsernameInput(profile.username);
  }, [profile?.username]);

  const canSave = useMemo(() => {
    return Boolean(profile?.id) && Boolean(username.trim()) && usernameStatus === "available";
  }, [profile?.id, username, usernameStatus]);

  const handleCharacterSelect = (index: number) => {
    setCharacterIndex(index);
  };

  const handleBackgroundSelect = (index: number) => {
    setBackgroundIndex(index);
  };

  const save = async () => {
    if (!profile?.id) return;
    setIsProcessing(true);
    setIsReady(true);

    try {
      // Username first (if changed)
      if (username.trim() && username.trim() !== (profile.username || "")) {
        const response = (await saveUsername({ username })) as
          | { data?: SetUsernameResult }
          | undefined;
        const result = response?.data;
        if (result?.ok === false) throw new Error(result.reason || "username_failed");
      }
      // Always persist parts
      await setParts(character, background);

      // Show success animation
      setTimeout(() => {
        onOpenChange(false);
        // No toast - the dialog closing is confirmation enough
      }, 1500);
    } catch (e: any) {
      setIsProcessing(false);
      setIsReady(false);
      const errorMessage = e?.message?.includes("username")
        ? "Name already taken!"
        : "Connection error!";
      showToast({
        title: "ERROR",
        description: errorMessage,
        variant: "error",
      });
    }
  };

  const skip = async () => {
      if (!profile?.id) return;
    try {
      await setParts(DEFAULT_PROFILE_CHARACTER, DEFAULT_PROFILE_BACKGROUND);
      onOpenChange(false);
      // No toast - smoother flow without interruption
    } catch (_e: any) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Custom overlay without gray padding */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Custom content without default dialog styles */}
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-3xl translate-x-[-50%] translate-y-[-50%]">
          {/* Main Container - thick TV bezel with depth */}
          <div
            className="relative w-full h-[70vh] min-h-[500px] rounded-lg p-3"
            style={{
              background:
                "linear-gradient(145deg, #4a4a52 0%, #3a3a42 30%, #2a2a32 70%, #1a1a22 100%)",
              boxShadow: `
                0 12px 40px rgba(0, 0, 0, 0.7),
                0 6px 20px rgba(0, 0, 0, 0.6),
                inset 0 3px 3px rgba(255, 255, 255, 0.2),
                inset 0 -3px 3px rgba(0, 0, 0, 0.7),
                0 0 0 1px rgba(255, 255, 255, 0.15)
              `.trim(),
              border: "3px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            {/* Inner bezel frame for depth */}
            <div
              className="relative w-full h-full rounded overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #1a1a22 0%, #0a0a12 100%)",
                boxShadow: `
                  inset 0 4px 8px rgba(0, 0, 0, 0.8),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.6),
                  0 1px 2px rgba(255, 255, 255, 0.1)
                `.trim(),
                border: "2px solid rgba(0, 0, 0, 0.6)",
              }}
            >
              {/* Inner content wrapper */}
              <div className="relative w-full h-full overflow-hidden">
                {/* Dynamic Background - fills entire dialog */}
                <div className="absolute inset-0 rounded-lg overflow-hidden">
                  <Image
                    src={background}
                    alt="Stage"
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 800px"
                    className="object-cover"
                    priority
                  />
                  {/* Dark overlay for readability */}
                  <div className="absolute inset-0 bg-black/50" />
                </div>

                {/* TV Overlay Effect */}
                <TVOverlay intensity="medium" />

                {/* Ready Flash Animation */}
                <AnimatePresence>
                  {isReady && (
                    <m.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{ duration: 0.5, times: [0, 0.5, 1] }}
                      className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center"
                    >
                      <m.div
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 0.5 }}
                        className="text-yellow-400 font-pressStart2P text-6xl"
                        style={{
                          textShadow: `
                      0 0 20px rgba(245,209,85,0.8),
                      0 0 40px rgba(245,209,85,0.6),
                      0 0 60px rgba(245,209,85,0.4)
                    `,
                        }}
                      >
                        READY!
                      </m.div>
                    </m.div>
                  )}
                </AnimatePresence>

                {/* Main Content */}
                <div className="relative h-full flex flex-col">
                  {/* Header */}
                  <div className="text-center pt-4 pb-1">
                    <h1
                      className="text-yellow-400 font-pressStart2P text-xl mb-2"
                      style={{
                        textShadow: `
                    2px 2px 0 #000,
                    4px 4px 8px rgba(0,0,0,0.8),
                    0 0 20px rgba(245,209,85,0.3)
                  `,
                      }}
                    >
                      CHARACTER SELECT
                    </h1>
                    <m.div
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-white font-pressStart2P text-[10px]"
                    >
                      <span className="text-red-500">P1</span> SELECT YOUR FIGHTER
                    </m.div>
                  </div>

                  {/* Main Layout */}
                  <div className="flex-1 flex items-center justify-center">
                    {/* Selection Panel */}
                    <div className="w-[450px] flex flex-col gap-3 items-center">
                      {/* Name Input Section */}
                      <div
                        className="w-full bg-black/60 border-2 border-yellow-400/80 p-1.5"
                        style={{
                          boxShadow: `
                      0 4px 8px rgba(0,0,0,0.6),
                      inset 0 1px 0 rgba(255,255,255,0.1),
                      inset 0 -1px 0 rgba(0,0,0,0.5)
                    `,
                        }}
                      >
                        <label className="text-yellow-400 font-pressStart2P text-xs mb-2 block text-center">
                          ENTER YOUR NAME
                        </label>
                        <div className="relative">
                          <Input
                            type="text"
                            value={username}
                            onChange={(e) => setUsernameInput(e.target.value)}
                            placeholder="PLAYER NAME"
                            maxLength={32}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-form-type="other"
                            className="w-full px-3 py-2 pr-10 bg-black/80 border-2 border-yellow-400/50 text-yellow-400 text-center font-pressStart2P text-xs tracking-wider focus:border-yellow-400 focus:outline-none focus:ring-0 focus:ring-offset-0 placeholder:text-yellow-400/30"
                            style={{
                              boxShadow: `
                          inset 0 2px 4px rgba(0,0,0,0.8),
                          0 1px 2px rgba(0,0,0,0.6)
                        `,
                            }}
                          />
                          {/* Username validation indicator */}
                          {usernameStatus && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              {usernameStatus === "checking" && (
                                <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                              )}
                              {usernameStatus === "available" && (
                                <m.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", bounce: 0.3 }}
                                >
                                  <Check className="w-4 h-4 text-green-400" />
                                </m.div>
                              )}
                              {usernameStatus === "taken" && (
                                <m.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", bounce: 0.3 }}
                                >
                                  <X className="w-4 h-4 text-red-500" />
                                </m.div>
                              )}
                              {(usernameStatus === "too-short" ||
                                usernameStatus === "too-long") && (
                                <m.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", bounce: 0.3 }}
                                >
                                  <AlertCircle className="w-4 h-4 text-orange-400" />
                                </m.div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Username status message */}
                        {usernameStatus && usernameStatus !== "checking" && (
                          <div className="mt-1 text-center">
                            {usernameStatus === "taken" && (
                              <span className="text-red-500 font-pressStart2P text-[8px]">
                                NAME TAKEN
                              </span>
                            )}
                            {usernameStatus === "too-short" && (
                              <span className="text-orange-400 font-pressStart2P text-[8px]">
                                TOO SHORT (MIN 2)
                              </span>
                            )}
                            {usernameStatus === "too-long" && (
                              <span className="text-orange-400 font-pressStart2P text-[8px]">
                                TOO LONG (MAX 32)
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Character Grid */}
                      <div className="w-full flex flex-col items-center">
                        <h3 className="text-white font-pressStart2P text-[10px] mb-1 text-center">
                          SELECT FIGHTER
                        </h3>
                        <div className="grid grid-cols-3 gap-px">
                          {PROFILE_CHARACTERS.map((char, index) => (
                            <m.button
                              key={char}
                              whileHover={{ scale: 1.05, y: -4 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleCharacterSelect(index)}
                              className="relative aspect-[3/4] overflow-hidden transition-shadow h-[95px]"
                              style={{
                                ...(characterIndex === index
                                  ? fighterStyles.portrait.selected
                                  : fighterStyles.portrait.base),
                              }}
                            >
                              <Image
                                src={char}
                                alt={CHARACTER_NAMES[index]}
                                fill
                                className="object-cover"
                              />
                              {/* Selection Indicator */}
                              {characterIndex === index && (
                                <m.div
                                  animate={{ opacity: [0.5, 1, 0.5] }}
                                  transition={{ duration: 1, repeat: Infinity }}
                                  className="absolute inset-0 border-4 border-yellow-400"
                                />
                              )}
                            </m.button>
                          ))}
                        </div>
                      </div>

                      {/* Stage Grid */}
                      <div className="w-full flex flex-col items-center">
                        <h3 className="text-white font-pressStart2P text-[10px] mb-1 text-center">
                          SELECT STAGE
                        </h3>
                        <div className="grid grid-cols-5 gap-px">
                          {PROFILE_BACKGROUNDS.map((bg, index) => (
                            <m.button
                              key={bg}
                              whileHover={{ scale: 1.08 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleBackgroundSelect(index)}
                              className="relative aspect-video overflow-hidden transition-shadow h-[36px]"
                              style={{
                                ...(backgroundIndex === index
                                  ? fighterStyles.stage.selected
                                  : fighterStyles.stage.base),
                              }}
                            >
                              <Image
                                src={bg}
                                alt={STAGE_NAMES[index]}
                                fill
                                className="object-cover opacity-80"
                              />
                              {backgroundIndex === index && (
                                <div className="absolute inset-0 border-2 border-yellow-400" />
                              )}
                            </m.button>
                          ))}
                        </div>
                        {/* Stage Name */}
                        <div className="mt-1 text-center">
                          <span className="text-gray-400 font-pressStart2P text-[9px]">
                            {STAGE_NAMES[backgroundIndex]}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-4 justify-center mt-2">
                        <m.button
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={skip}
                          className="px-5 py-2 text-gray-300 font-pressStart2P text-[10px]"
                          style={fighterStyles.button.skip}
                        >
                          SKIP
                        </m.button>

                        <m.button
                          whileHover={canSave && !isProcessing ? { scale: 1.02, y: -2 } : {}}
                          whileTap={canSave && !isProcessing ? { scale: 0.98 } : {}}
                          onClick={save}
                          disabled={!canSave || isProcessing}
                          className="px-8 py-2 text-black font-pressStart2P text-[10px]"
                          style={{
                            ...fighterStyles.button.start,
                            opacity: !canSave || isProcessing ? 0.5 : 1,
                            cursor: !canSave || isProcessing ? "not-allowed" : "pointer",
                          }}
                        >
                          {isProcessing ? (
                            <m.span
                              animate={{ opacity: [1, 0.5, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                            >
                              LOADING...
                            </m.span>
                          ) : (
                            "START GAME"
                          )}
                        </m.button>
                      </div>
                    </div>
                  </div>

                  {/* Character Preview - Positioned at bottom left, flush with bottom */}
                  <AnimatePresence mode="wait">
                    <m.div
                      key={character}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: 50, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="absolute -bottom-4 left-0 w-[200px] h-[240px] pointer-events-none z-10"
                    >
                      <Image
                        src={character}
                        alt="Fighter"
                        fill
                        className="object-contain object-bottom drop-shadow-2xl"
                        priority
                      />
                      {/* Character Name Label - positioned bottom right with text shadow */}
                      <div className="absolute bottom-2 right-0">
                        <span
                          className="text-yellow-400 font-pressStart2P text-[12px] uppercase"
                          style={{
                            textShadow: `
                            2px 2px 0 #000,
                            4px 4px 8px rgba(0,0,0,0.8),
                            0 0 20px rgba(245,209,85,0.3)
                          `.trim(),
                          }}
                        >
                          {CHARACTER_NAMES[characterIndex]}
                        </span>
                      </div>
                    </m.div>
                  </AnimatePresence>

                  {/* Henlo Corp System ID - Top Left */}
                  <div className="absolute top-2 left-4 opacity-50">
                    <div className="text-gray-600 font-pressStart2P text-[6px]">
                      SYSTEM ID: HNL-0451
                    </div>
                  </div>

                  {/* Henlo Corp Branding - Bottom Right */}
                  <div className="absolute bottom-2 right-4 text-right opacity-60">
                    <div className="text-gray-500 font-pressStart2P text-[7px] leading-relaxed">
                      <div>FIGHTER DATABASE v2.1</div>
                      <div className="text-gray-600 mt-0.5">© HENLO CORP ENTERTAINMENT</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export default ProfileSetupDialog;
